import { useEffect, useId, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, ApiErrorCodes } from '@/api';
import type { RevocationReason } from '@/api/sessionSignals';
import { useSession } from '@/app/session';
import { safeReturnPath } from '@/app/guards';
import { heldForSignInCount, subscribeOutbox } from '@/features/outbox/store';
import { PasswordInput } from '@/components/PasswordInput';
import './login.css';

/**
 * Login screen.
 *
 * Pass-through auth: the backend attempts an IMAP/SMTP LOGIN against whichever
 * host the admin routing table resolves for the address's domain. Nothing is
 * validated client-side beyond "looks like an email", and no password is ever
 * stored here.
 *
 * On success the backend sets the three session cookies; this view never sees
 * them and never needs to. It hands the response to `SessionProvider`, which is
 * the only thing that tracks "are we signed in", and navigates to `?from=` —
 * the path the guard saved when it bounced the user here.
 */
export function LoginView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, status, binding, revocation } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The error is read with the fields it is about (WCAG 3.3.1): a screen
  // reader landing back on either field hears why.
  const errorId = useId();
  const passwordId = useId();
  const described = error !== null ? { 'aria-describedby': errorId, 'aria-invalid': true } : {};
  const [lockedFor, setLockedFor] = useState<number | null>(null);

  const returnTo = safeReturnPath(searchParams.get('from'));
  const heldOutbox = useHeldOutboxCount();

  // A live countdown, so a locked-out user can see it lift rather than guessing.
  useEffect(() => {
    if (lockedFor === null || lockedFor <= 0) return;
    const timer = setTimeout(
      () => setLockedFor((value) => (value === null ? null : value - 1)),
      1_000,
    );
    return () => clearTimeout(timer);
  }, [lockedFor]);

  // Already signed in (a bookmarked /login, or another tab signed us in).
  if (status === 'authenticated') return <Navigate to={returnTo} replace />;

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login({ email, password, remember });
      navigate(returnTo, { replace: true });
    } catch (cause) {
      if (cause instanceof ApiError && cause.isRateLimited) {
        setLockedFor(cause.retryAfter ?? 300);
        setError('Too many failed attempts from this address.');
      } else if (cause instanceof ApiError && cause.code === ApiErrorCodes.DomainNotAllowed) {
        // The routing table refused before any credential was checked. That is
        // a different problem from a wrong password and deserves its own copy.
        setError(cause.message || `Mail for that domain isn't hosted here.`);
      } else if (cause instanceof ApiError && cause.code === 'device_key_required') {
        // `session.binding: required` and this browser cannot hold a device
        // key (ADR 0047). Nothing the user types will change that, so the copy
        // has to say what *would* — not repeat "sign-in failed" at them.
        setError(
          'This browser can’t store the key that protects a session on this device, so sign-in is not allowed here. Try a different browser, or allow this site to store data (private windows and blocked site data are the usual causes).',
        );
      } else if (cause instanceof ApiError && cause.code === ApiErrorCodes.InvalidCredentials) {
        // Relayed verbatim: with pass-through auth the mail host's own message
        // ("account disabled", "password expired") is the useful one.
        setError(cause.message);
      } else {
        setError(cause instanceof Error ? cause.message : 'Sign-in failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  const lockedOut = lockedFor !== null && lockedFor > 0;

  return (
    <main className="login">
      <form className="login__card card" onSubmit={(event) => void onSubmit(event)}>
        <img className="login__logo" src="/icons/icon.svg" alt="" width={48} height={48} />
        <h1 className="login__title">Sign in to OLS Mail</h1>
        <p className="muted login__subtitle">Use your full email address and mailbox password.</p>
        {/*
          * Which server these credentials go to. The desktop app can point at
          * any deployment and testing looks like production, so say it where
          * the password is typed.
          */}
        <p className="subtle login__server" data-testid="login-server">
          Server: <strong>{window.location.host}</strong>
        </p>

        {/*
          * Why this device is here at all, when the server ended its session
          * from somewhere else (`session_revoked`). Without it a password
          * change on another device reads as a random sign-out — and the old
          * password, which the user will try first, can only fail. Kept up
          * beside a failed attempt: that is exactly when it is most useful.
          */}
        {revocation !== null && (
          <p className="login__notice" role="status">
            {revocationNotice(revocation)}
          </p>
        )}

        {/*
          * Unsent mail kept on this device after a revocation (ADR 0061 §4).
          * Said here because the person reading it is the one who can make it
          * go: it sends as soon as the account that wrote it signs back in.
          * Only the number is shown — whoever is at this screen may not be
          * that person, and nothing else about the messages is theirs to see.
          */}
        {heldOutbox > 0 && (
          <p className="login__notice" role="status" data-testid="login-held-outbox">
            {heldOutbox === 1
              ? '1 unsent message will send after you sign in.'
              : `${heldOutbox} unsent messages will send after you sign in.`}{' '}
            They are kept on this device for the account that wrote them, and are never shown to
            or sent from a different account.
          </p>
        )}

        {error !== null && (
          <p className="login__error" role="alert" id={errorId}>
            {error}
            {lockedOut && ` Try again in ${formatWait(lockedFor)}.`}
          </p>
        )}

        <label className="login__field">
          <span>Email address</span>
          <input
            className="input"
            type="email"
            autoComplete="username"
            required
            {...described}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
          />
        </label>

        {/* Labelled by `for`, not by wrapping: a wrapping label would add the
            Show password button's name to the field's own. */}
        <div className="login__field">
          <label htmlFor={passwordId}>Password</label>
          <PasswordInput
            id={passwordId}
            autoComplete="current-password"
            required
            {...described}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        {/*
          * Said before the password is typed, not after (ADR 0042): a browser
          * that cannot hold a key gets a shorter session that cannot be
          * remembered, and under `binding: required` no session at all. Either
          * way it is the user's to know, and hiding it is the silent
          * degradation the ADR is written against.
          */}
        {!binding.supported && (
          <p className="login__error" role="status">
            This browser can’t protect your session from cookie theft — it has no usable key
            storage. You can still sign in if the server allows it, but the session will be short
            and cannot be kept signed in.
          </p>
        )}

        <label className="login__remember">
          <input
            type="checkbox"
            checked={remember && binding.supported}
            disabled={!binding.supported}
            onChange={(event) => setRemember(event.target.checked)}
          />
          <span>Keep me signed in on this device</span>
        </label>

        <button
          type="submit"
          className="btn btn--primary login__submit"
          disabled={busy || lockedOut}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="subtle login__footnote">
          Your credentials are checked directly against your mail server. This client never stores
          them.
        </p>
      </form>
    </main>
  );
}

/**
 * How many unsent messages this device is keeping for a signed-out owner, read
 * from disk and re-read whenever the Outbox changes — the revocation's
 * teardown finishes keeping the queue after this screen is already up.
 */
function useHeldOutboxCount(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let live = true;
    const read = (): void => {
      void heldForSignInCount().then((next) => {
        if (live) setCount(next);
      });
    };
    read();
    const unsubscribe = subscribeOutbox(read);
    return () => {
      live = false;
      unsubscribe();
    };
  }, []);
  return count;
}

/** What the sign-in screen says after a `session_revoked`. */
function revocationNotice(reason: RevocationReason): string {
  switch (reason) {
    case 'password_changed':
      return 'Your password was changed on another device. Sign in with your new password.';
    case 'signed_out_elsewhere':
      return 'This device was signed out from another session. Sign in again.';
    case 'unknown':
      return 'You were signed out from another session.';
  }
}

function formatWait(seconds: number | null): string {
  if (seconds === null) return 'a few minutes';
  if (seconds < 60) return `${seconds} seconds`;
  return `${Math.ceil(seconds / 60)} minutes`;
}
